// API Service
// Centralized fetch wrapper with auth, CSRF policy, and envelope handling

import config from './config.js';
import { auth } from './auth.js';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * API Service Class
 */
export class ApiService {
    constructor() {
        this.baseUrl = config.apiUrl;
    }

    buildUrl(endpoint) {
        if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
            return endpoint;
        }
        return `${this.baseUrl}${endpoint}`;
    }

    async request(endpoint, options = {}) {
        const url = this.buildUrl(endpoint);
        const method = (options.method || 'GET').toUpperCase();
        const isFormData = options.body instanceof FormData;

        const headers = {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...options.headers,
        };

        const accessToken = auth.getAccessToken();
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        if (!SAFE_METHODS.has(method)) {
            const csrfToken = localStorage.getItem(config.tokens.csrf);
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }
        }

        const response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers,
        });

        const csrfHeader = response.headers.get('x-csrf-token');
        if (csrfHeader) {
            localStorage.setItem(config.tokens.csrf, csrfHeader);
        }

        if (
            response.status === 401 &&
            !options._retried &&
            ![
                config.endpoints.login,
                config.endpoints.register,
                config.endpoints.refresh,
                config.endpoints.forgotPassword,
                config.endpoints.resetPassword,
            ].includes(endpoint)
        ) {
            const refreshed = await auth.refreshSession();
            if (refreshed) {
                return this.request(endpoint, { ...options, _retried: true });
            }
            auth.logout({ revokeServerSession: false, redirectTo: 'login.html' });
        }

        const contentType = response.headers.get('content-type') || '';
        const body = contentType.includes('application/json')
            ? await response.json().catch(() => null)
            : null;

        if (!response.ok) {
            const err = body?.error || {};
            const error = {
                status: response.status,
                code: err.code || 'UNKNOWN_ERROR',
                message: err.message || `Request failed with status ${response.status}`,
                details: err.details,
                requiresSubscription:
                    err.code === 'SUBSCRIPTION_REQUIRED' ||
                    err.code === 'TRIAL_QUIZ_LIMIT_REACHED',
            };
            throw error;
        }

        if (body && typeof body.success === 'boolean') {
            return body;
        }

        return {
            success: true,
            data: body,
        };
    }

    get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }

    post(endpoint, data = undefined, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'POST',
            body: data !== undefined ? JSON.stringify(data) : undefined,
        });
    }

    patch(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
}

export const api = new ApiService();
